import { useState, useEffect, useCallback } from "react";
import { authFetch, API } from "@/App";
import { AlertTriangle, CheckCircle, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function SafetyPage() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: "Near Miss", location: "", description: "", severity: "LOW", action: "" });

  const loadIncidents = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/factory/safety-incidents`);
      const json = await res.json();
      if (json.success) setIncidents(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadIncidents(); }, [loadIncidents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/factory/safety-incidents`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (json.success) {
        toast.success("Incident reported");
        setShowForm(false);
        setForm({ type: "Near Miss", location: "", description: "", severity: "LOW", action: "" });
        loadIncidents();
      }
    } catch (e) { toast.error("Failed to report"); }
  };

  const closeIncident = async (incidentId) => {
    try {
      const res = await authFetch(`${API}/factory/safety-incidents/${incidentId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "CLOSED" }),
      });
      if ((await res.json()).success) { toast.success("Incident closed"); loadIncidents(); }
    } catch (e) { toast.error("Failed to close"); }
  };

  const openCount = incidents.filter((i) => i.status === "OPEN").length;
  const daysSafe = (() => {
    const highMed = incidents.filter((i) => ["HIGH", "MEDIUM"].includes(i.severity));
    if (highMed.length === 0) return 365;
    const latest = new Date(Math.max(...highMed.map((i) => new Date(i.date))));
    return Math.floor((new Date() - latest) / (1000 * 60 * 60 * 24));
  })();

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="safety-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Safety</h1>
          <p className="text-slate-400 text-sm mt-1">Incident tracking and safety compliance</p>
        </div>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <button className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="report-incident">
              <Plus className="w-4 h-4" /> Report Incident
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#1C2B2B] border-emerald-900/30 text-white">
            <DialogHeader><DialogTitle>Report Safety Incident</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400">Type</label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm"><option>Near Miss</option><option>Minor Injury</option><option>Major Injury</option><option>Equipment Failure</option><option>Fire</option><option>Chemical Spill</option></select></div>
                <div><label className="text-xs text-slate-400">Severity</label><select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm"><option value="LOW">Low</option><option value="MEDIUM">Medium</option><option value="HIGH">High</option></select></div>
              </div>
              <div><label className="text-xs text-slate-400">Location</label><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Kiln Area" className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required /></div>
              <div><label className="text-xs text-slate-400">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" required /></div>
              <div><label className="text-xs text-slate-400">Corrective Action</label><input value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} className="w-full mt-1 px-3 py-2 bg-[#0F1A1A] border border-emerald-900/30 rounded-lg text-white text-sm" /></div>
              <button type="submit" className="w-full py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="submit-incident">Report Incident</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-4" data-testid="safety-metric-0"><p className="text-slate-400 text-xs uppercase">Days Since Last Incident</p><p className="text-emerald-400 text-2xl font-bold mt-1">{daysSafe}</p></div>
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-4" data-testid="safety-metric-1"><p className="text-slate-400 text-xs uppercase">Total Incidents</p><p className="text-amber-400 text-2xl font-bold mt-1">{incidents.length}</p></div>
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-4" data-testid="safety-metric-2"><p className="text-slate-400 text-xs uppercase">Open Investigations</p><p className="text-red-400 text-2xl font-bold mt-1">{openCount}</p></div>
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-4" data-testid="safety-metric-3"><p className="text-slate-400 text-xs uppercase">Closed</p><p className="text-emerald-400 text-2xl font-bold mt-1">{incidents.length - openCount}</p></div>
      </div>

      <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-emerald-900/30"><h3 className="text-white font-semibold">Incident Log</h3></div>
        <table className="w-full" data-testid="safety-incidents-table">
          <thead>
            <tr className="border-b border-emerald-900/30">
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">Type</th>
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">Location</th>
              <th className="text-left px-4 py-3 text-xs text-slate-400 uppercase">Description</th>
              <th className="text-center px-4 py-3 text-xs text-slate-400 uppercase">Severity</th>
              <th className="text-center px-4 py-3 text-xs text-slate-400 uppercase">Status</th>
              <th className="text-center px-4 py-3 text-xs text-slate-400 uppercase">Action</th>
            </tr>
          </thead>
          <tbody>
            {incidents.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-slate-500">No incidents recorded</td></tr>}
            {incidents.map((inc) => (
              <tr key={inc.incident_id} className="border-b border-emerald-900/20 hover:bg-white/[0.02]">
                <td className="px-4 py-3 text-sm text-slate-300">{inc.date}</td>
                <td className="px-4 py-3 text-sm text-white">{inc.type}</td>
                <td className="px-4 py-3 text-sm text-slate-300">{inc.location}</td>
                <td className="px-4 py-3 text-sm text-slate-300 max-w-[250px] truncate">{inc.description}</td>
                <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${inc.severity === "HIGH" ? "bg-red-600/20 text-red-400" : inc.severity === "MEDIUM" ? "bg-amber-600/20 text-amber-400" : "bg-slate-600/20 text-slate-400"}`}>{inc.severity}</span></td>
                <td className="px-4 py-3 text-center"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${inc.status === "OPEN" ? "bg-amber-600/20 text-amber-400" : "bg-emerald-600/20 text-emerald-400"}`}>{inc.status === "OPEN" ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />} {inc.status}</span></td>
                <td className="px-4 py-3 text-center">{inc.status === "OPEN" && <button onClick={() => closeIncident(inc.incident_id)} className="px-2 py-1 bg-emerald-600/20 text-emerald-400 rounded text-xs hover:bg-emerald-600/30">Close</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
